import React from 'react'
import { useTranslation } from 'react-i18next'
import { Search } from 'lucide-react'
import { UnderlineTabs } from '@/components/ui/underline-tabs'
import { cn } from '@/lib/utils'
import { useChat, useChatBadge } from './ChatProvider'
import { ChatHome } from './ChatHome'
import { PeoplePanel } from './ChatPeople'
import { ChatSettingsMenu } from './ChatSettingsMenu'

export type ChatInboxTab = 'conversations' | 'people'

/** What sits beside the tabs; a function gets `compact` when the row is too narrow for the full pill. */
export type ChatInboxStatusSlot = React.ReactNode | ((compact: boolean) => React.ReactNode)

/* Space between the tabs and the status pill. */
const ROW_GAP = 8

interface Props {
  /** F-191 phase 5: the community hides the Pessoas tab (kept while a link opened it). */
  hidePeople?: boolean
  tab: ChatInboxTab
  onTabChange: (tab: ChatInboxTab) => void
  /** Called once a person or a conversation was picked: the host closes itself. */
  onPicked: () => void
  /** F-184: the host shows the settings gear elsewhere (the avatar panel's top line) and drives the blocked list. */
  showSettings?: boolean
  blocksOpen?: boolean
  onBlocksOpenChange?: (open: boolean) => void
  /** On the Conversas | Pessoas tab row, at the right: the avatar panel puts my status here (David, 2026-10-29). */
  statusSlot?: ChatInboxStatusSlot
  className?: string
}

/**
 * F-184: the chat's "Conversas | Pessoas" list with its search, the online count
 * and the ⋯ settings (read receipts, sounds, blocked people). It used to live in
 * the bottom "Mensagens" bar; it now lives in the sidebar the header avatar
 * opens (`UserMenu`). Picking someone opens the docked window (bottom right) or
 * the full-screen conversation, and calls `onPicked`.
 */
export const ChatInbox = ({ hidePeople = false, tab, onTabChange, onPicked, showSettings = true, blocksOpen: blocksOpenProp, onBlocksOpenChange, statusSlot, className }: Props) => {
  const { t } = useTranslation('chat')
  const chat = useChat()
  const badge = useChatBadge()
  const [query, setQuery] = React.useState('')
  const [ownBlocksOpen, setOwnBlocksOpen] = React.useState(false)
  const blocksOpen = blocksOpenProp ?? ownBlocksOpen
  const setBlocksOpen = (open: boolean) => (onBlocksOpenChange ? onBlocksOpenChange(open) : setOwnBlocksOpen(open))
  const available = Boolean(chat.me?.chatAvailable)
  const onlineCount = available ? chat.onlineTotal ?? chat.online.length : 0
  const searchLabel = tab === 'people' ? t('people.search') : t('dock.searchConversations')
  const compact = useCompactStatus()
  const status = typeof statusSlot === 'function' ? statusSlot(compact.value) : statusSlot

  const openConversation = (id: string) => {
    chat.showConversation(id).catch(() => {})
    onPicked()
  }

  return (
    <div className={cn('flex h-full min-h-0 flex-col', className)} data-chat-inbox>
      <div className="shrink-0 border-b border-gray-200">
        <div className="flex items-center gap-1 p-2 pb-0">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchLabel}
              aria-label={searchLabel}
              className="w-full rounded-md bg-gray-100 py-1.5 pl-8 pr-2 text-sm focus:bg-white focus:outline-none focus:ring-1 focus:ring-primary"
              data-chat-inbox-search
            />
          </div>
          {showSettings && <ChatSettingsMenu onOpenBlocks={() => { setBlocksOpen(true); onTabChange('conversations') }} />}
        </div>
        {/* The tabs on the left and my status on the right, one line (David, 2026-10-30). */}
        <div ref={compact.rowRef} className="flex items-end justify-between px-2" style={{ gap: ROW_GAP }}>
          <UnderlineTabs size="sm" value={tab} onChange={onTabChange} aria-label={t('hub.title')} data-testid="chat-inbox-view"
            className="min-w-0 flex-1" listClassName="border-b-0"
            items={[
              { value: 'conversations' as ChatInboxTab, label: t('tabs.conversations'), count: badge > 0 ? badge : undefined, testId: 'chat-inbox-tab-conversations' },
              /* F-191 phase 5: the community hides the Pessoas shortcut (the directory stays reachable by its URL). */
              ...(hidePeople && tab !== 'people' ? [] : [{ value: 'people' as ChatInboxTab, label: t('people.title'), count: onlineCount > 0 ? onlineCount : undefined, testId: 'chat-inbox-tab-people' }]),
            ]} />
          {status && <div ref={compact.slotRef} className="shrink-0 self-center" data-user-panel-status>{status}</div>}
        </div>
      </div>
      <div className="min-h-0 flex-1">
        {tab === 'conversations'
          ? <ChatHome hideStatus query={query} blocksOpen={blocksOpen} onCloseBlocks={() => setBlocksOpen(false)} onOpenConversation={openConversation} />
          : <PeoplePanel embedded externalQuery={query} onClose={onPicked} />}
      </div>
    </div>
  )
}

/**
 * Whether the status pill has to shrink to its dot: the row is narrower than the
 * tabs' natural width plus the full pill. The full pill's width is remembered
 * from the last time it was shown, so the switch does not flip back and forth.
 */
const useCompactStatus = () => {
  const rowRef = React.useRef<HTMLDivElement | null>(null)
  const slotRef = React.useRef<HTMLDivElement | null>(null)
  const fullWidth = React.useRef(0)
  const [value, setValue] = React.useState(false)

  const measure = React.useCallback(() => {
    const row = rowRef.current
    const slot = slotRef.current
    const triggers = row?.querySelectorAll<HTMLElement>('[role="tab"]')
    if (!row || !slot || !triggers?.length) return
    if (!slot.querySelector('[data-chat-status-compact]')) fullWidth.current = slot.offsetWidth
    /* The tabs' own width (the list stretches to fill the row, so its size says nothing). */
    const first = triggers[0]
    const last = triggers[triggers.length - 1]
    const tabsWidth = last.offsetLeft + last.offsetWidth - first.offsetLeft
    const style = getComputedStyle(row)
    const inner = row.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight)
    const next = inner < tabsWidth + fullWidth.current + ROW_GAP
    setValue((prev) => (prev === next ? prev : next))
  }, [])

  /* After every render: a count or a status name changes the widths. */
  React.useLayoutEffect(measure)

  React.useEffect(() => {
    const row = rowRef.current
    if (!row) return
    const resize = new ResizeObserver(measure)
    resize.observe(row)
    return () => resize.disconnect()
  }, [measure])

  return { rowRef, slotRef, value }
}
